import React, { useState, useEffect } from 'react';
import SlideoutPanel from './Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import AddBookingForm from './Forms/AddBookingForm';
import PageHeader from './Common/PageHeader';
import apiService from '../services/api';

const BookingsView = ({ mockData, setMockData, showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Map BookingStatusType enum (int) to string
  const mapStatus = (status) => {
    // API returns numeric enum: 0=Pending, 1=Confirmed, 2=Cancelled, 3=Completed, 4=NoShow
    const map = { 0: 'pending', 1: 'confirmed', 2: 'cancelled', 3: 'completed', 4: 'noshow' };
    if (typeof status === 'number') return map[status] || 'pending';
    if (typeof status === 'string') return status.toLowerCase();
    return 'pending';
  };

  // Transform API AppointmentDTO → component shape
  const transformAppointment = (a) => {
    // AppointmentDate is a date, StartTime is a TimeSpan string e.g. "11:00:00"
    const datePart = a.appointmentDate
      ? new Date(a.appointmentDate).toISOString().split('T')[0]
      : '';
    const timePart = a.startTime
      ? String(a.startTime).substring(0, 5)   // "11:00:00" → "11:00"
      : '00:00';
    const endTimePart = a.endTime
      ? String(a.endTime).substring(0, 5)
      : timePart;

    // Compute duration from startTime/endTime or use Duration field
    let durationLabel = '';
    if (a.duration && a.duration > 0) {
      durationLabel = `${a.duration} min`;
    } else {
      // fallback: parse from time strings
      const [sh, sm] = timePart.split(':').map(Number);
      const [eh, em] = endTimePart.split(':').map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      durationLabel = mins > 0 ? `${mins} min` : '';
    }

    return {
      id: a.appointmentNumber || a.id,
      patient: a.customerName || '',
      practitioner: a.practitionerName || '',
      service: a.serviceName || '',
      datetime: datePart && timePart ? `${datePart} ${timePart}` : '',
      status: mapStatus(a.status),
      duration: durationLabel,
      location: a.locationName || ''
    };
  };

  // Fetch bookings from API
  const fetchBookings = async () => {
    setLoading(true);
    setApiError(false);
    try {
      const response = await apiService.getAppointments({ pageSize: 200 });
      const data = Array.isArray(response) ? response : (response.items || response.data || []);
      setBookings(data.map(transformAppointment));
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setApiError(true);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter bookings based on search and status
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = searchTerm === '' || 
      booking.patient.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.practitioner.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Slideout handlers
  const handleAddBooking = () => {
    const formContent = (
      <AddBookingForm 
        mockData={mockData}
        onSubmit={handleCreateBooking}
        onCancel={closeSlideout}
      />
    );
    
    openSlideout('Create New Booking', formContent, '750px');
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    const formContent = (
      <AddBookingForm 
        mockData={mockData}
        initialData={booking}
        onSubmit={(data) => handleUpdateBooking(booking.id, data)}
        onCancel={closeSlideout}
        isEditing={true}
      />
    );
    
    openSlideout('Edit Booking', formContent, '750px');
  };

  const handleViewBooking = (booking) => {
    const detailContent = (
      <BookingDetail 
        booking={booking} 
        onEdit={() => handleEditBooking(booking)}
      />
    );
    
    openSlideout(`Booking ${booking.id}`, detailContent, '600px');
  };

  // CRUD operations
  const handleCreateBooking = async (bookingData) => {
    try {
      closeSlideout();
      showNotification('Booking created successfully!', 'success');
      fetchBookings();
    } catch (error) {
      showNotification('Error creating booking. Please try again.', 'error');
    }
  };

  const handleUpdateBooking = async (bookingId, bookingData) => {
    try {
      closeSlideout();
      setEditingBooking(null);
      showNotification('Booking updated successfully!', 'success');
      fetchBookings();
    } catch (error) {
      showNotification('Error updating booking. Please try again.', 'error');
    }
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    const dateStr = date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short' 
    });
    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    return `${dateStr}, ${timeStr}`;
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'confirmed': return 'status-confirmed';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-pending';
    }
  };

  return (
    <div className="bookings-view">
      <PageHeader pageKey="bookings">
        <button className="btn btn-secondary">
          <span className="icon icon--export icon--default"></span>
          Export
        </button>
        <button className="btn btn-primary" onClick={handleAddBooking}>
          <span className="icon icon--add icon--success"></span>
          New Booking
        </button>
      </PageHeader>

      {/* Error state */}
      {apiError && (
        <div className="empty-state" style={{ margin: '16px 0' }}>
          <div className="empty-state-icon">⚠️</div>
          <h3>Could not load bookings</h3>
          <p>There was a problem connecting to the server.</p>
          <button className="btn btn-primary" onClick={fetchBookings}>Retry</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{bookings.length}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{bookings.filter(b => b.status === 'confirmed').length}</div>
            <div className="stat-label">Confirmed</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-number">{bookings.filter(b => b.status === 'pending').length}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Bookings ({filteredBookings.length})</h3>
        </div>
        
        {/* Filters */}
        <div className="filters-bar">
          <div className="filter-group">
            <input
              type="search"
              className="search-input"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select 
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="card-content">
          <div className="material-table-container">
            <div className="material-table-header">
              <h3 className="material-table-title">Bookings Overview</h3>
              <span className="material-table-meta">Showing {filteredBookings.length} bookings</span>
            </div>

            <table className="material-table">
              <thead>
                <tr>
                  <th className="material-th">ID</th>
                  <th className="material-th">Patient</th>
                  <th className="material-th">Practitioner</th>
                  <th className="material-th">Service</th>
                  <th className="material-th">Date & Time</th>
                  <th className="material-th">Duration</th>
                  <th className="material-th">Location</th>
                  <th className="material-th">Status</th>
                  <th className="material-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ padding: 0, border: 'none' }}>
                      <div className="empty-state">
                        <div className="empty-state-icon">⏳</div>
                        <h3>Loading bookings...</h3>
                      </div>
                    </td>
                  </tr>
                ) : filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ padding: 0, border: 'none' }}>
                      <div className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <h3>{searchTerm || statusFilter !== 'all' ? 'No matches found' : 'No bookings yet'}</h3>
                        <p>{searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Bookings will appear here once patients start scheduling appointments.'}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <tr key={booking.id} className="material-tr">
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.id}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.patient}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.practitioner}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.service}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{formatDateTime(booking.datetime)}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.duration}</span>
                      </td>
                      <td className="material-cell">
                        <span className="material-body-medium">{booking.location}</span>
                      </td>
                      <td className="material-cell">
                        <span
                          className={`material-chip--${booking.status === 'confirmed' ? 'success' : booking.status === 'cancelled' ? 'error' : 'warning'}`}
                          data-tooltip={
                            booking.status === 'confirmed' ? 'Confirmed - Booking is confirmed and scheduled' :
                            booking.status === 'cancelled' ? 'Cancelled - Booking has been cancelled' :
                            booking.status === 'pending' ? 'Pending - Awaiting confirmation' :
                            booking.status.charAt(0).toUpperCase() + booking.status.slice(1)
                          }
                        >
                          {booking.status === 'confirmed' ? '✓' : booking.status === 'cancelled' ? '✗' : booking.status === 'pending' ? '⏳' : '?'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-icons">
                          <button
                            className="action-icon action-icon--view"
                            onClick={() => handleViewBooking(booking)}
                            data-tooltip="View Booking"
                          ></button>
                          <button
                            className="action-icon action-icon--edit"
                            onClick={() => handleEditBooking(booking)}
                            data-tooltip="Edit Booking"
                          ></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Booking Detail Component
const BookingDetail = ({ booking, onEdit }) => {
  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    const dateStr = date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    return { dateStr, timeStr };
  };

  const { dateStr, timeStr } = formatDateTime(booking.datetime);

  return (
    <div className="booking-detail">
      <div className="detail-sections">
        {/* Basic Info */}
        <div className="detail-section">
          <h4>Booking Information</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Booking ID</label>
              <span>{booking.id}</span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <span className={`status-badge status-${booking.status}`}>
                {booking.status}
              </span>
            </div>
          </div>
        </div>

        {/* Patient & Practitioner */}
        <div className="detail-section">
          <h4>Patient & Practitioner</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Patient Name</label>
              <span>{booking.patient}</span>
            </div>
            <div className="detail-item">
              <label>Practitioner</label>
              <span>{booking.practitioner}</span>
            </div>
          </div>
        </div>

        {/* Appointment Details */}
        <div className="detail-section">
          <h4>Appointment Details</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Service</label>
              <span>{booking.service}</span>
            </div>
            <div className="detail-item">
              <label>Duration</label>
              <span>{booking.duration}</span>
            </div>
            <div className="detail-item">
              <label>Date</label>
              <span>{dateStr}</span>
            </div>
            <div className="detail-item">
              <label>Time</label>
              <span>{timeStr}</span>
            </div>
            <div className="detail-item">
              <label>Location</label>
              <span>{booking.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-actions">
        <button className="btn btn-primary" onClick={onEdit}>
          Edit Booking
        </button>
      </div>
    </div>
  );
};

export default BookingsView;